import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import Bowser from "bowser";
import { receiverVersion } from '../helpers/globalConfig';
import { locationSearch, routerCustomerId, routerAppName } from '../helpers/utils'
import IcaFileRequestHelpers from '../helpers/icaFileHelpers';
import { getBrowserId, getPasscodeToken, getToken, getUsername, saveUsername, saveToken,
    savePasscodeToken, removeUsername, removeToken, removePasscodeToken } from '../services/storage/launchStorage';
import { requestIcafile, requestIcafileTicket } from '../services/http/icafileRequest';
import { ErrorCodeEnum } from '../enums/errorCode';
import { CurrentComponentEnum } from '../enums/currentComponent';
import { IIcaFileResponse, IPolicyModel } from '../interface/IIcafile.interface';
import PasscodeLoginComponent from '../components/passcodeLogin/passcodeLogin';
import ErrorComponent from '../components/error/error';
import ProgressComponent from '../components/progress/progress';
import SessionComponent from '../components/session/session';
import LoginComponent from '../components/login/login';

let credentials = new IcaFileRequestHelpers();

const LaunchContainer = () => {
    const [activeComponent, setActiveComponent] = useState(CurrentComponentEnum.Progress);
    const [appName, setAppName] = useState(routerAppName);
    const [appIcon, setAppIcon] = useState('');
    const [singleTry, setSingleTry] = useState(false);
    const [icaFileTicket, setIcaFileTicket] = useState('');
    const [icaFile, setIcaFile] = useState('');
    const [icaData, setIcaData] = useState('');
    const [icaPolicies, setIcaPolicies] = useState({} as IPolicyModel);
    const [errorCode, setErrorCode] = useState(ErrorCodeEnum.Default);
    const [loginError, setLoginError] = useState('');
    const [loginBtnLoading, setLoginBtnLoading] = useState(false);
    
    let retryAttempt = 0;

    const initViewport = {
        width: window.innerWidth,
        height: window.innerHeight
    }

    useEffect(() => {
        if (appName !== 'HDXEngine.html') {
            init();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appName]);

    const init = () => {
        const actualReceiverVersion = locationSearch('receiverVersion') ? locationSearch('receiverVersion') : receiverVersion;
        
        const bowser = Bowser.getParser(window.navigator.userAgent);
        credentials.isMobile = bowser.getPlatformType() === 'mobile';
        credentials.browserId = getBrowserId();
        if (getPasscodeToken()) {
            credentials.token = getPasscodeToken();
            credentials.isAuthenticated = false;
            credentials.hasSharedPassword = true;
        } else {
            credentials.token = getToken();
            if (credentials.token) {
                credentials.isAuthenticated = true;
                credentials.username = getUsername();
            }
        }

        credentials.receiverVersion = actualReceiverVersion;
        credentials.fastLaunch = false;
        credentials.fastLaunch = locationSearch('fastLaunch') !== "false";

        if (locationSearch('url')) {
          credentials.redirectionUrl = decodeURI(locationSearch('url'));
        }
        if (locationSearch('singleTry')) {
            setSingleTry(true);
        }
        
        getIcaFile();
    }

    const getIcaFileTicket = () => {
        credentials.getRequest(credentials).then((request) => {
            if (request) {
                requestIcafileTicket(routerCustomerId, appName, request).then((res) => {
                    handleIcafileSuccess(res?.data)
                }).catch(error => {
                    handleIcafileError(error);
                });
            }
            else{
                handleIcafileError('Failed to get request');
                console.error('Failed to get request');
            }
        }).catch(error => {
            console.error('Error in getRequest:', error);
        });
    };
    const getIcaFile = () => {
        icaPolicies && icaPolicies?.nativeReceiver
            ? getIcaFileTicket()
            : credentials.getRequest(credentials).then((request) => {
                if (request) {
                    requestIcafile(routerCustomerId, routerAppName, request).then((res) => {
                        handleIcafileSuccess(res?.data)
                    }).catch(error => {
                        handleIcafileError(error);
                    })
                }
                else{
                    handleIcafileError('Failed to get request');
                    console.error('Failed to get request');
                }
            }).catch(error => {
                console.error('Error in getRequest:', error);
            });
    };
    
    const handleIcafileSuccess = (resData: IIcaFileResponse) => {
        setLoginBtnLoading(false);
        if (resData.token !== null) {
            if (credentials.isAuthenticated) {
                credentials?.username && saveUsername(credentials?.username);
                saveToken(resData.token || '');
            } else {
                resData.token && savePasscodeToken(resData.token);
            }
        }

        if (resData.icaFileTicket) {
            setIcaFileTicket(resData.icaFileTicket);
        } else {
            setIcaFile(resData.icaFile);
            resData.icaData && setIcaData(resData.icaData);
        }

        setAppName(resData.friendlyName?.replaceAll(' ', '%20') || '');
        setAppIcon(resData.icon || '');
        resData.policies && setIcaPolicies(resData.policies);
    
        launchSession();
    }


    const retryLaunches = (errorStatus?: number) => {
        if (!singleTry && retryAttempt < 2) {
            console.log(`ICA file attempt ${retryAttempt} failed. Trying again.`); // NOSONAR
            retryAttempt++;
            getIcaFile();
        }
        if (typeof errorStatus !== "undefined") {
            setErrorCode(errorStatus)
        }
        setActiveComponent(CurrentComponentEnum.Error);
    }

    const showLogin = () => {
        setActiveComponent(CurrentComponentEnum.Login);
        setLoginError('');
        credentials.hasSharedPassword = false;
        credentials.requestToken = credentials.username != null;
    }
    
    const showPasscodeLogin = () => {
        setActiveComponent(CurrentComponentEnum.PasscodeLogin);
        setLoginError('');
        credentials.hasSharedPassword = true;
    }

    const doLogin = (values: any) => {
        setLoginBtnLoading(true);
        credentials = Object.assign(credentials, values)
        if (!credentials.requestToken) {
            removeUsername();
            removeToken();
            removePasscodeToken();
        }
        retryAttempt++;
        setErrorCode(ErrorCodeEnum.ResourceProviderUnreachable);
        setLoginError('');
        credentials.isAuthenticated = false;
        getIcaFile();
    }

    const handleIcafileError = (error: any) => {
        setLoginBtnLoading(false);
        const errorRes = error?.response;
        const errorStatus = errorRes?.status;
        setErrorCode(errorStatus);
        switch (errorStatus) {
            case ErrorCodeEnum.Unauthorized:
                setActiveComponent(CurrentComponentEnum.Error);
                break;
            case ErrorCodeEnum.ResourceNotFound:
                if (activeComponent === CurrentComponentEnum.Progress) {
                    if (errorRes.data && errorRes.data.parameters) {
                        const catalogsError = errorRes.data.parameters.filter((p: any) => p.name === 'reason');
                        if (catalogsError.length !== 0) {
                            setActiveComponent(CurrentComponentEnum.Error);
                            if (errorRes.data.parameters[0].value === 'noCatalogsDefined') {
                                setErrorCode(ErrorCodeEnum.ResourceNotFound);
                            }
                        }
                    } else if (errorRes.data.search(/Service Unavailable/) > 0) {
                        return retryLaunches(ErrorCodeEnum.ResourceProviderUnreachable);
                    }
                }
                break;
            case ErrorCodeEnum.Unauthenticated:
                if (activeComponent === CurrentComponentEnum.Progress) {
                    if (!errorRes.data)
                        break;

                    if (errorRes.data.customProperties) {
                        const name = errorRes.data.customProperties.filter((p: any) => p.name === 'friendlyName');
                        if (name.length !== 0) {
                            setAppName(name[0].value.replaceAll(' ', '%20'));
                        }
                        const icon = errorRes.data.customProperties.filter((p: any) => p.name === 'icon');
                        if (icon.length !== 0)
                            setAppIcon(icon[0].value);

                        const errorItem = errorRes.data.customProperties.filter((p: any) => p.name === 'error');
                        if (errorItem.length !== 0) {
                            setActiveComponent(CurrentComponentEnum.Error);
                            if (errorItem[0].value !== 'invalidgrant') {
                                setErrorCode(ErrorCodeEnum.NotFound);
                            }
                            break;
                        }
                    }
                    const requestedProperties = errorRes?.data.parameters.filter((p: any) => p.name === 'property').map((p: any) => p.value);
                    credentials.disableReputation = !requestedProperties.some((p: any) => p === 'reputationToken');
                    if (requestedProperties.some((p: any) => p === 'athenaAuthorizationHeader')) {
                        if (!errorRes)
                            break;

                        if (errorRes.data.customProperties) {
                            const authdomain = errorRes.data.customProperties.filter((p: any) => p.name === 'authdomain');
                            if (authdomain.length !== 0) {
                                window.location.href = window.location.pathname + "/login?authdomain=" + authdomain[0].value;
                            }
                        }
                    } else if (requestedProperties.some((p: any) => p === 'isAuthenticated')) {
                        showLogin();
                    } else if (requestedProperties.some((p: any) => p === 'password')) {
                        showPasscodeLogin();
                    }
                    break;
                } else if (activeComponent === 'passcodeLogin') {
                    setLoginError('PASSCODE_INCORRECT');
                } else {
                    setLoginError('LOGIN_INCORRECT');
                }
                break;
            case ErrorCodeEnum.TooManyRequests:
                setActiveComponent(CurrentComponentEnum.Error);
                break;
            default:
                retryLaunches();
                break;
        }
    }

    // launch session
    const launchSession = () => {
        setActiveComponent(CurrentComponentEnum.Session);
    }

  return (
      <>
        <div className='launch-container' data-testid='launch-page'>
            <Helmet>
                <title>{ appName }</title>
                <link rel="icon" href={ appIcon } />
            </Helmet>
            { activeComponent === CurrentComponentEnum.Progress && <ProgressComponent /> }
            { activeComponent === CurrentComponentEnum.Session &&
                <SessionComponent
                    icaFile={ icaFile }
                    icaData={ icaData }
                    icaPolicies={ icaPolicies }
                    icaFileTicket={ icaFileTicket }
                    singleTry={ singleTry }
                    initViewport={ initViewport }
                /> }
            { activeComponent === CurrentComponentEnum.PasscodeLogin
                && <PasscodeLoginComponent doLogin={ doLogin } loginError={ loginError } loading={loginBtnLoading}/> }
            { activeComponent === CurrentComponentEnum.Login
                && <LoginComponent doLogin={ doLogin } loginError={ loginError } loading={loginBtnLoading}/> }
            { activeComponent === CurrentComponentEnum.Error
                && <ErrorComponent errorCode={ errorCode }/> }
        </div>
      </>

  )
}

export default LaunchContainer;
